import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import {Subscription} from 'rxjs';
import {LoaderService} from '../../../services/loader.service';
import {WPPostBlog} from '../../../types/WPPostBlog';
import {ToolsService} from '../../../services/tools.service';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {finalize} from 'rxjs/operators';
import {StaticBlogService} from '../../../services/static-blog.service';

@Component({
  selector: 'app-blog-puffs-list',
  templateUrl: './blog-puffs-list.component.html',
  styleUrls: ['./blog-puffs-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlogPuffsListComponent implements OnInit, OnChanges, OnDestroy {
  @Input() tags: number[];
  @Input() puffsLimit: number;
  @Input() excludeId: number;
  @Input() cpruTypes: number[];
  @Input() serviceTypes: number[];
  @Input() places: number[];
  @Input() excludedIds: number[];
  @Output() puffsNumberUpdated = new EventEmitter<number>();

  public puffs: WPPostBlog[] = [];
  private subscriptions: Subscription[] = [];
  private loadedTags: number[];
  private loadedPuffsLimit: number;
  private loadedExcludeId: number;
  private loadedCpruTypes: number[];
  private loadedServiceTypes: number[];
  private loadedPlaces: number[];
  private puffsCalculatedLimit: number;
  private isLoading: boolean = false;

  constructor(
    private loaderService: LoaderService,
    private blogService: StaticBlogService,
    private tools: ToolsService,
    private _change: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.updatePuffsLimit();
    this.loadPuffs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.updatePuffsLimit();
    this.loadPuffs();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public trackByFn(i: number, el: WPPostBlog) {
    return el ? el.id : null;
  }

  private updatePuffsLimit(): void {
    if (!this.tools.isNil(this.excludeId)) {
      this.puffsCalculatedLimit = this.puffsLimit + 1;
    } else {
      this.puffsCalculatedLimit = this.puffsLimit;
    }
  }

  private loadPuffs(): void {
    if (
      this.isLoading === false &&
      (this.puffs.length === 0 ||
        this.loadedExcludeId !== this.excludeId ||
        this.loadedPuffsLimit !== this.puffsLimit ||
        !this.tools.isEqual(this.loadedTags, this.tags) ||
        !this.tools.isEqual(this.loadedCpruTypes, this.cpruTypes) ||
        !this.tools.isEqual(this.loadedServiceTypes, this.serviceTypes) ||
        !this.tools.isEqual(this.loadedPlaces, this.places))
    ) {
      this.updatePuffs();
    }
  }

  private updatePuffs(cutConditions?: boolean): void {
    this.isLoading = true;
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    if (!this.tools.isNil(this.tags) && this.tags.length > 0) {
      this.subscriptions.push(
        this.blogService
          .GetBlogByTag(1, this.tags, this.excludedIds)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processBlogResult(result, cutConditions, this.puffsCalculatedLimit),
            (error) => {
              this.isLoading = false;
              console.error(error);
            },
          ),
      );
    } else if (
      !cutConditions &&
      ((!this.tools.isNil(this.cpruTypes) && this.cpruTypes.length > 0) ||
        (!this.tools.isNil(this.serviceTypes) && this.serviceTypes.length > 0) ||
        (!this.tools.isNil(this.places) && this.places.length > 0))
    ) {
      this.subscriptions.push(
        this.blogService
          .GetBlogByCategories(this.cpruTypes, this.serviceTypes, this.places, this.excludedIds)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processBlogResult(result, cutConditions, this.puffsCalculatedLimit),
            (error) => {
              this.isLoading = false;
              console.error(error);
            },
          ),
      );
    } else {
      this.subscriptions.push(
        this.blogService
          .GetBlogLatest(1, this.puffsCalculatedLimit, this.excludedIds)
          .pipe(
            finalize(() => {
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => this.processBlogResult(result, cutConditions, this.puffsCalculatedLimit),
            (error) => {
              this.isLoading = false;
              console.error(error);
            },
          ),
      );
    }

    this.loadedExcludeId = this.excludeId;
    this.loadedPuffsLimit = this.puffsLimit;
    this.loadedTags = this.tags;
    this.loadedCpruTypes = this.cpruTypes;
    this.loadedServiceTypes = this.serviceTypes;
    this.loadedPlaces = this.places;
  }

  private processBlogResult(result: WPPostBlog[], cutConditions: boolean, limit: number): void {
    this.isLoading = false;

    if (!cutConditions && (result === null || result.length === 0)) {
      this.updatePuffs(true);
    } else {
      if (!this.tools.isNil(this.excludeId)) {
        this.puffs = result.filter((item) => item.id.toString() !== this.excludeId.toString());
      } else {
        this.puffs = result;
      }
      this.puffs = this.puffs.filter((item) => this.filterPost(item));
      this.puffs = this.puffs.slice(0, this.puffsLimit);
      this.puffsNumberUpdated.emit(this.puffs.length);
    }
    this._change.markForCheck();
  }

  private filterPost(item: WPPostBlog): boolean {
    return item.acf.isPreview === false || item.acf.isPreview == null;
  }
}
